import React from "react";
import { useNavigate } from "react-router-dom";

const Card = ({ title, description, action, onClick, disabled }) => (
  <div
    style={{
      background: "#fff",
      borderRadius: 10,
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      padding: 16,
      display: "flex",
      flexDirection: "column",
      gap: 8,
    }}
  >
    <div style={{ fontWeight: 700 }}>{title}</div>
    <div style={{ color: "#475569", fontSize: 14 }}>{description}</div>
    <div style={{ marginTop: 6 }}>
      <button
        disabled={disabled}
        onClick={onClick}
        style={{
          background: disabled ? "#e5e7eb" : "#2563eb",
          color: disabled ? "#6b7280" : "#fff",
          border: "none",
          borderRadius: 6,
          padding: "10px 14px",
          fontWeight: 600,
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        {action}
      </button>
    </div>
  </div>
);

export default function AdminTools() {
  const [busy, setBusy] = React.useState(false);
  const apiUrl = process.env.REACT_APP_API_URL || "";
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = React.useState(null);
  const [users, setUsers] = React.useState([]);
  const [usersBusy, setUsersBusy] = React.useState(false);
  const [newUser, setNewUser] = React.useState({
    email: "",
    firstName: "",
    lastName: "",
    role: "counselor",
    schoolCode: "",
    phoneNumber: "",
  });
  const [selectedId, setSelectedId] = React.useState("");
  const [showAddSchool, setShowAddSchool] = React.useState(false);
  const [showCodes, setShowCodes] = React.useState(false);

  React.useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${apiUrl.replace(/\/$/, "")}/me`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        });
        if (!res.ok) throw new Error("Failed to fetch user profile");
        const data = await res.json();
        setUserProfile(data);
      } catch {
        setUserProfile(null);
      }
    };
    fetchProfile();
  }, [apiUrl]);

  const fetchUsers = React.useCallback(async () => {
    if (!userProfile) return;
    if (!["admin", "districtAdmin"].includes(userProfile.role)) return;
    setUsersBusy(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${apiUrl.replace(/\/$/, "")}/admin/users`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      if (!res.ok) throw new Error("Failed to fetch users");
      const data = await res.json();
      setUsers(Array.isArray(data.users) ? data.users : []);
    } catch (e) {
      setUsers([]);
    } finally {
      setUsersBusy(false);
    }
  }, [apiUrl, userProfile]);

  React.useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("token");
      await fetch(`${apiUrl.replace(/\/$/, "")}/logout`, {
        method: "POST",
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
    } catch {}
    localStorage.removeItem("token");
    navigate("/login");
  };

  const exportNotesCsv = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${apiUrl.replace(/\/$/, "")}/notes`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      const data = await res.json();
      const notes = Array.isArray(data.notes) ? data.notes : [];
      const rows = [
        [
          "noteId",
          "studentId",
          "ownerEmail",
          "createdAt",
          "topic",
          "content",
          "sharedWith",
        ],
        ...notes.map((n) => [
          n.noteId,
          n.studentId,
          n.ownerEmail,
          n.createdAt,
          (n.topic || "").replaceAll("\n", " "),
          (n.content || "").replaceAll("\n", " "),
          Array.isArray(n.sharedWith) ? n.sharedWith.join(";") : "",
        ]),
      ];
      const csv = rows
        .map((r) =>
          r.map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`).join(",")
        )
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `notes_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  };

  const exportStudentsCsv = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${apiUrl.replace(/\/$/, "")}/students`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      const data = await res.json();
      const students = Array.isArray(data.students) ? data.students : [];
      const rows = [
        [
          "studentId",
          "firstName",
          "lastName",
          "grade",
          "referredBy",
          "referredReason",
          "studentSupportStatus",
          "meetings",
        ],
        ...students.map((s) => [
          s.studentId,
          s.firstName,
          s.lastName,
          s.grade,
          s.referredBy,
          s.referredReason,
          Array.isArray(s.studentSupportStatus)
            ? s.studentSupportStatus.join(";")
            : String(s.studentSupportStatus || ""),
          Array.isArray(s.meetings) ? s.meetings.join(";") : "",
        ]),
      ];
      const csv = rows
        .map((r) =>
          r.map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`).join(",")
        )
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `students_export_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  };

  const exportAuditLogsCsv = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${apiUrl.replace(/\/$/, "")}/admin/audit-logs`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      const data = await res.json();
      const logs = Array.isArray(data.logs) ? data.logs : [];
      const rows = [
        [
          "timestamp",
          "userEmail",
          "displayName",
          "ipAddress",
          "action",
          "noteId",
          "studentId",
        ],
        ...logs.map((l) => [
          l.timestamp,
          l.userEmail,
          l.displayName || "",
          l.ipAddress || "",
          l.action || "",
          l.noteId || "",
          l.studentId || "",
        ]),
      ];
      const csv = rows
        .map((r) =>
          r.map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`).join(",")
        )
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  };

  const exportConsentsCsv = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${apiUrl.replace(/\/$/, "")}/admin/consents`, {
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      const data = await res.json();
      const notes = Array.isArray(data.notes) ? data.notes : [];
      const rows = [
        [
          "noteId",
          "studentId",
          "ownerEmail",
          "consentTimestamp",
          "consentMethod",
          "sharedWith",
        ],
        ...notes.map((n) => [
          n.noteId,
          n.studentId,
          n.ownerEmail,
          n.consentTimestamp || "",
          n.consentMethod || "",
          Array.isArray(n.sharedWith) ? n.sharedWith.join(";") : "",
        ]),
      ];
      const csv = rows
        .map((r) =>
          r.map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`).join(",")
        )
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `consents_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  };

  const exportArchivedDeletedCsv = async () => {
    setBusy(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${apiUrl.replace(/\/$/, "")}/admin/archived-deleted`,
        {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        }
      );
      const data = await res.json();
      const records = Array.isArray(data.records) ? data.records : [];
      const rows = [
        [
          "noteId",
          "studentId",
          "ownerEmail",
          "createdAt",
          "archived",
          "deleted",
          "deleteAfter",
        ],
        ...records.map((n) => [
          n.noteId,
          n.studentId,
          n.ownerEmail,
          n.createdAt,
          Boolean(n.archived),
          Boolean(n.deleted),
          n.deleteAfter || "",
        ]),
      ];
      const csv = rows
        .map((r) =>
          r.map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`).join(",")
        )
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `archived_deleted_${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ background: "#f3f4f6", minHeight: "100vh" }}>
      <nav
        style={{
          background: "linear-gradient(90deg, #23395d 60%, #2d4a7a 100%)",
          color: "#fff",
          padding: "0 0 0 32px",
          display: "flex",
          alignItems: "center",
          height: 64,
          fontWeight: 500,
          fontSize: 18,
          letterSpacing: 0.5,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 22, marginRight: 40 }}>
          COUNSELOR NOTES
        </span>
        <button
          style={{
            color: "#fff",
            background: "none",
            border: "none",
            textDecoration: "none",
            marginRight: 32,
            font: "inherit",
            cursor: "pointer",
          }}
          onClick={() => navigate("/students")}
        >
          Home
        </button>
        <button
          style={{
            color: "#fff",
            background: "none",
            border: "none",
            textDecoration: "none",
            marginRight: 32,
            font: "inherit",
            cursor: "pointer",
          }}
          onClick={() => navigate("/support-plans")}
        >
          Support Plans
        </button>
        <button
          style={{
            color: "#fff",
            background: "none",
            border: "none",
            textDecoration: "none",
            marginRight: 32,
            font: "inherit",
            cursor: "pointer",
          }}
          onClick={() => navigate("/analysis")}
        >
          Analysis
        </button>
        {userProfile?.role === "admin" && (
          <button
            style={{
              color: "#fff",
              background: "none",
              border: "1px solid rgba(255,255,255,0.6)",
              textDecoration: "none",
              marginRight: 12,
              font: "inherit",
              cursor: "pointer",
              borderRadius: 6,
              padding: "6px 10px",
            }}
            onClick={() => navigate("/admin-tools")}
          >
            Admin Tools
          </button>
        )}
        <button
          onClick={handleLogout}
          style={{
            background: "#fff",
            color: "#23395d",
            border: "none",
            borderRadius: 6,
            fontWeight: 700,
            fontSize: 16,
            padding: "8px 22px",
            cursor: "pointer",
            marginLeft: "auto",
            marginRight: 24,
          }}
        >
          Log Out
        </button>
      </nav>
      <div style={{ maxWidth: 920, margin: "0 auto", padding: 24 }}>
        <h1 style={{ marginTop: 12 }}>Admin Tools</h1>
        {/* Record Management Tools */}
        {["admin", "districtAdmin"].includes(userProfile?.role) && (
          <div style={{ marginTop: 18, marginBottom: 18 }}>
            <div
              style={{
                background: "#fff",
                borderRadius: 10,
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                padding: 16,
              }}
            >
              <div style={{ fontWeight: 700, fontSize: 16 }}>
                Record Management Tools
              </div>
              <div style={{ color: "#475569", fontSize: 14, marginTop: 4 }}>
                Ensure FERPA rights for amendment, deletion, and data
                minimization.
              </div>

              {/* Find IDs helper */}
              <div style={{ marginTop: 12 }}>
                <FindIdsPanel apiUrl={apiUrl} onUseId={setSelectedId} />
              </div>

              {/* Archive Record */}
              <div style={{ marginTop: 14 }}>
                <div style={{ fontWeight: 600 }}>Archive Record</div>
                <div style={{ color: "#64748b", fontSize: 13 }}>
                  Moves student or note into read-only, inactive status (for
                  retention compliance).
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    marginTop: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <ArchiveControls apiUrl={apiUrl} selectedId={selectedId} />
                </div>
              </div>

              {/* Delete Record */}
              <div style={{ marginTop: 16 }}>
                <div style={{ fontWeight: 600 }}>Delete Record</div>
                <div style={{ color: "#64748b", fontSize: 13 }}>
                  Performs soft or hard delete, logs event in AuditLog.
                  <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>
                    Soft delete: marks the record as deleted but keeps it for
                    auditing and potential restoration. Hidden from normal
                    views.
                    <br />
                    Hard delete: permanently removes the record and cannot be
                    undone. Blocked if a legal hold is active (for notes).
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    marginTop: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <DeleteControls apiUrl={apiUrl} selectedId={selectedId} />
                </div>
              </div>

              {/* Data Retention Settings */}
              {["admin", "districtAdmin"].includes(userProfile?.role) && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 600 }}>Data Retention Settings</div>
                  <div style={{ color: "#64748b", fontSize: 13 }}>
                    Admin sets how long records are kept before automatic
                    archival/deletion (e.g., 5 years).
                  </div>
                  <RetentionControls
                    apiUrl={apiUrl}
                    isDistrict={userProfile?.role === "districtAdmin"}
                  />
                </div>
              )}

              {/* Transfer Student Data */}
              {userProfile?.role === "districtAdmin" && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontWeight: 600 }}>Transfer Student Data</div>
                  <div style={{ color: "#64748b", fontSize: 13 }}>
                    Allows exporting and transferring student data securely to
                    another school.
                  </div>
                  <TransferControls apiUrl={apiUrl} />
                </div>
              )}
            </div>
          </div>
        )}
        {["admin", "districtAdmin"].includes(userProfile?.role) && (
          <div style={{ marginTop: 18, marginBottom: 18 }}>
            <div
              style={{
                background: "#fff",
                borderRadius: 10,
                boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                padding: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>
                    Access Control & Permissions
                  </div>
                  <div style={{ color: "#475569", fontSize: 14 }}>
                    Control who can view or edit records. Manage roles:
                    Counselor, Admin, District Admin, Auditor.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={fetchUsers}
                  style={{
                    padding: "6px 10px",
                    background: "#0ea5e9",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                >
                  {usersBusy ? "Refreshing..." : "Refresh"}
                </button>
              </div>

              {/* Add user form */}
              <div
                style={{
                  marginTop: 12,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
                  gap: 8,
                }}
              >
                <input
                  placeholder="Email"
                  value={newUser.email}
                  onChange={(e) =>
                    setNewUser({ ...newUser, email: e.target.value })
                  }
                  style={{
                    padding: 8,
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                  }}
                />
                <input
                  placeholder="First name"
                  value={newUser.firstName}
                  onChange={(e) =>
                    setNewUser({ ...newUser, firstName: e.target.value })
                  }
                  style={{
                    padding: 8,
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                  }}
                />
                <input
                  placeholder="Last name"
                  value={newUser.lastName}
                  onChange={(e) =>
                    setNewUser({ ...newUser, lastName: e.target.value })
                  }
                  style={{
                    padding: 8,
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                  }}
                />
                {userProfile?.role === "districtAdmin" && (
                  <input
                    placeholder="School code"
                    value={newUser.schoolCode}
                    onChange={(e) =>
                      setNewUser({ ...newUser, schoolCode: e.target.value })
                    }
                    style={{
                      padding: 8,
                      borderRadius: 6,
                      border: "1px solid #e5e7eb",
                    }}
                  />
                )}
                <select
                  value={newUser.role}
                  onChange={(e) =>
                    setNewUser({ ...newUser, role: e.target.value })
                  }
                  style={{
                    padding: 8,
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                  }}
                >
                  <option value="counselor">Counselor</option>
                  <option value="admin">Admin</option>
                  <option value="districtAdmin">District Admin</option>
                  <option value="auditor">Auditor</option>
                </select>
                <input
                  placeholder="Phone (optional)"
                  value={newUser.phoneNumber}
                  onChange={(e) =>
                    setNewUser({ ...newUser, phoneNumber: e.target.value })
                  }
                  style={{
                    padding: 8,
                    borderRadius: 6,
                    border: "1px solid #e5e7eb",
                  }}
                />
                <div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem("token");
                        const res = await fetch(
                          `${apiUrl.replace(/\/$/, "")}/admin/users`,
                          {
                            method: "POST",
                            headers: {
                              "Content-Type": "application/json",
                              Authorization: token ? `Bearer ${token}` : "",
                            },
                            body: JSON.stringify(newUser),
                          }
                        );
                        const data = await res.json();
                        if (!res.ok)
                          throw new Error(
                            data.error || "Failed to create user"
                          );
                        // Show temp password and refresh list
                        alert(
                          `User created. Temporary password: ${data.tempPassword}`
                        );
                        setNewUser({
                          email: "",
                          firstName: "",
                          lastName: "",
                          role: "counselor",
                          schoolCode: "",
                          phoneNumber: "",
                        });
                        fetchUsers();
                      } catch (err) {
                        alert(err.message);
                      }
                    }}
                    style={{
                      padding: "8px 12px",
                      background: "#22c55e",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6,
                      cursor: "pointer",
                      fontWeight: 600,
                    }}
                  >
                    Add User
                  </button>
                </div>
              </div>

              {/* Users table */}
              <div style={{ marginTop: 16, overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ textAlign: "left", background: "#f8fafc" }}>
                      <th
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #e5e7eb",
                        }}
                      >
                        Email
                      </th>
                      <th
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #e5e7eb",
                        }}
                      >
                        Name
                      </th>
                      <th
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #e5e7eb",
                        }}
                      >
                        Role
                      </th>
                      <th
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #e5e7eb",
                        }}
                      >
                        School
                      </th>
                      <th
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #e5e7eb",
                        }}
                      >
                        Verified
                      </th>
                      <th
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #e5e7eb",
                        }}
                      >
                        Status
                      </th>
                      <th
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #e5e7eb",
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.email}>
                        <td
                          style={{
                            padding: 8,
                            borderBottom: "1px solid #f1f5f9",
                          }}
                        >
                          {u.email}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderBottom: "1px solid #f1f5f9",
                          }}
                        >
                          {u.firstName} {u.lastName}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderBottom: "1px solid #f1f5f9",
                          }}
                        >
                          <select
                            value={u.role}
                            onChange={async (e) => {
                              const newRole = e.target.value;
                              try {
                                const token = localStorage.getItem("token");
                                const res = await fetch(
                                  `${apiUrl.replace(
                                    /\/$/,
                                    ""
                                  )}/admin/users/${encodeURIComponent(
                                    u.email
                                  )}`,
                                  {
                                    method: "PUT",
                                    headers: {
                                      "Content-Type": "application/json",
                                      Authorization: token
                                        ? `Bearer ${token}`
                                        : "",
                                    },
                                    body: JSON.stringify({ role: newRole }),
                                  }
                                );
                                const data = await res.json();
                                if (!res.ok)
                                  throw new Error(
                                    data.error || "Failed to update"
                                  );
                                setUsers(
                                  users.map((x) =>
                                    x.email === u.email
                                      ? { ...x, role: newRole }
                                      : x
                                  )
                                );
                              } catch (err) {
                                alert(err.message);
                              }
                            }}
                            style={{
                              padding: 6,
                              borderRadius: 6,
                              border: "1px solid #e5e7eb",
                            }}
                          >
                            <option value="counselor">Counselor</option>
                            <option value="admin">Admin</option>
                            <option value="districtAdmin">
                              District Admin
                            </option>
                            <option value="auditor">Auditor</option>
                          </select>
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderBottom: "1px solid #f1f5f9",
                          }}
                        >
                          {u.schoolCode}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderBottom: "1px solid #f1f5f9",
                          }}
                        >
                          {u.verified ? "Yes" : "No"}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderBottom: "1px solid #f1f5f9",
                          }}
                        >
                          {u.disabled ? "Disabled" : "Active"}
                        </td>
                        <td
                          style={{
                            padding: 8,
                            borderBottom: "1px solid #f1f5f9",
                          }}
                        >
                          {!u.disabled ? (
                            <button
                              type="button"
                              onClick={async () => {
                                if (
                                  !window.confirm(
                                    "Disable this user? They will be logged out."
                                  )
                                )
                                  return;
                                try {
                                  const token = localStorage.getItem("token");
                                  const res = await fetch(
                                    `${apiUrl.replace(
                                      /\/$/,
                                      ""
                                    )}/admin/users/${encodeURIComponent(
                                      u.email
                                    )}`,
                                    {
                                      method: "DELETE",
                                      headers: {
                                        Authorization: token
                                          ? `Bearer ${token}`
                                          : "",
                                      },
                                    }
                                  );
                                  const data = await res.json();
                                  if (!res.ok)
                                    throw new Error(
                                      data.error || "Failed to disable"
                                    );
                                  setUsers(
                                    users.map((x) =>
                                      x.email === u.email
                                        ? { ...x, disabled: true }
                                        : x
                                    )
                                  );
                                } catch (err) {
                                  alert(err.message);
                                }
                              }}
                              style={{
                                padding: "6px 10px",
                                background: "#ef4444",
                                color: "#fff",
                                border: "none",
                                borderRadius: 6,
                                cursor: "pointer",
                              }}
                            >
                              Disable
                            </button>
                          ) : (
                            <span style={{ color: "#64748b" }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {users.length === 0 && (
                      <tr>
                        <td
                          colSpan={7}
                          style={{ padding: 12, color: "#64748b" }}
                        >
                          {usersBusy ? "Loading users…" : "No users found"}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 16,
            marginTop: 12,
          }}
        >
          {["admin", "districtAdmin"].includes(userProfile?.role) && (
            <Card
              title="Add School"
              description="Create a new school and generate a secure school code for signups."
              action="Open"
              onClick={() => setShowAddSchool(true)}
            />
          )}
          <Card
            title="Export Notes (CSV)"
            description="Download a CSV of all notes you have access to for auditing."
            action={busy ? "Preparing..." : "Download CSV"}
            onClick={exportNotesCsv}
            disabled={busy}
          />
          <Card
            title="Export Students (CSV)"
            description="Download a CSV of all students and key fields."
            action={busy ? "Preparing..." : "Download CSV"}
            onClick={exportStudentsCsv}
            disabled={busy}
          />
          <Card
            title="Export Audit Logs (CSV)"
            description="Every access, view, or edit event (user, IP, timestamp, noteId/studentId)."
            action={busy ? "Preparing..." : "Download CSV"}
            onClick={exportAuditLogsCsv}
            disabled={busy}
          />
          <Card
            title="Export Consents / Authorizations (CSV)"
            description="Parental or eligible student consents for data sharing."
            action={busy ? "Preparing..." : "Download CSV"}
            onClick={exportConsentsCsv}
            disabled={busy}
          />
          <Card
            title="Export Archived / Deleted Records (CSV)"
            description="Archived/deleted records for compliance verification or investigation."
            action={busy ? "Preparing..." : "Download CSV"}
            onClick={exportArchivedDeletedCsv}
            disabled={busy}
          />
          <Card
            title="Export Report (PDF)"
            description="Generate a printable PDF of summary reports (coming soon)."
            action="Coming soon"
            onClick={() => {}}
            disabled
          />
        </div>
      </div>
      {/* Add School Modal */}
      {showAddSchool && (
        <AddSchoolModal
          apiUrl={apiUrl}
          onClose={() => setShowAddSchool(false)}
        />
      )}
      {showCodes && (
        <SchoolCodesModal apiUrl={apiUrl} onClose={() => setShowCodes(false)} />
      )}
    </div>
  );
}

// Archive Controls Component
function ArchiveControls({ apiUrl, selectedId }) {
  const [type, setType] = React.useState("student");
  const [id, setId] = React.useState("");
  React.useEffect(() => {
    if (selectedId) setId(selectedId);
  }, [selectedId]);
  const doArchive = async () => {
    if (!id.trim()) return alert("Enter an ID");
    try {
      const token = localStorage.getItem("token");
      const base = apiUrl.replace(/\/$/, "");
      const url =
        type === "student"
          ? `${base}/students/${encodeURIComponent(id)}/archive`
          : `${base}/notes/${encodeURIComponent(id)}/archive`;
      const res = await fetch(url, {
        method: "POST",
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      alert("Archived successfully");
    } catch (e) {
      alert(e.message);
    }
  };
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <select
        value={type}
        onChange={(e) => setType(e.target.value)}
        style={{ padding: 8, borderRadius: 6, border: "1px solid #e5e7eb" }}
      >
        <option value="student">Student</option>
        <option value="note">Note</option>
      </select>
      <input
        placeholder="ID"
        value={id}
        onChange={(e) => setId(e.target.value)}
        style={{
          padding: 8,
          borderRadius: 6,
          border: "1px solid #e5e7eb",
          minWidth: 220,
        }}
      />
      <button
        type="button"
        onClick={doArchive}
        style={{
          padding: "8px 12px",
          background: "#2563eb",
          color: "#fff",
          border: "none",
          borderRadius: 6,
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        Archive
      </button>
    </div>
  );
}

// Delete Controls Component
function DeleteControls({ apiUrl, selectedId }) {
  const [type, setType] = React.useState("student");
  const [mode, setMode] = React.useState("soft");
  const [id, setId] = React.useState("");
  React.useEffect(() => {
    if (selectedId) setId(selectedId);
  }, [selectedId]);
  const doDelete = async () => {
    if (!id.trim()) return alert("Enter an ID");
    const extra =
      mode === "hard"
        ? " This action is PERMANENT and cannot be undone."
        : " This record will be hidden from normal views.";
    if (
      !window.confirm(
        `Are you sure you want to ${mode.toUpperCase()} delete this ${type}?${extra}`
      )
    )
      return;
    try {
      const token = localStorage.getItem("token");
      const base = apiUrl.replace(/\/$/, "");
      let url = "";
      let method = "DELETE";
      if (type === "student" && mode === "soft")
        url = `${base}/students/${encodeURIComponent(id)}`;
      else if (type === "student" && mode === "hard")
        url = `${base}/admin/students/${encodeURIComponent(id)}/hard`;
      else if (type === "note" && mode === "soft")
        url = `${base}/notes/${encodeURIComponent(id)}`;
      else if (type === "note" && mode === "hard")
        url = `${base}/admin/notes/${encodeURIComponent(id)}/hard`;
      const res = await fetch(url, {
        method,
        headers: { Authorization: token ? `Bearer ${token}` : "" },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      alert("Deleted successfully");
    } catch (e) {
      alert(e.message);
    }
  };
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      <select
        value={type}
        onChange={(e) => setType(e.target.value)}
        style={{ padding: 8, borderRadius: 6, border: "1px solid #e5e7eb" }}
      >
        <option value="student">Student</option>
        <option value="note">Note</option>
      </select>
      <select
        value={mode}
        onChange={(e) => setMode(e.target.value)}
        style={{ padding: 8, borderRadius: 6, border: "1px solid #e5e7eb" }}
      >
        <option value="soft">Soft Delete</option>
        <option value="hard">Hard Delete</option>
      </select>
      <input
        placeholder="ID"
        value={id}
        onChange={(e) => setId(e.target.value)}
        style={{
          padding: 8,
          borderRadius: 6,
          border: "1px solid #e5e7eb",
          minWidth: 220,
        }}
      />
      <button
        type="button"
        onClick={doDelete}
        style={{
          padding: "8px 12px",
          background: "#ef4444",
          color: "#fff",
          border: "none",
          borderRadius: 6,
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        Delete
      </button>
    </div>
  );
}

// Find IDs helper component
function FindIdsPanel({ apiUrl, onUseId }) {
  const [open, setOpen] = React.useState(false);
  const [tab, setTab] = React.useState("students");
  const [query, setQuery] = React.useState("");
  const [students, setStudents] = React.useState([]);
  const [notes, setNotes] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const base = apiUrl.replace(/\/$/, "");
      const [sRes, nRes] = await Promise.all([
        fetch(`${base}/students`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        }),
        fetch(`${base}/notes`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        }),
      ]);
      const sData = await sRes.json();
      const nData = await nRes.json();
      setStudents(Array.isArray(sData.students) ? sData.students : []);
      let ns = Array.isArray(nData.notes) ? nData.notes : [];
      ns.sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );
      setNotes(ns.slice(0, 200));
    } catch {
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  React.useEffect(() => {
    if (open && (students.length === 0 || notes.length === 0)) load();
  }, [open, students.length, notes.length, load]);

  const lower = query.trim().toLowerCase();
  const filteredStudents = students
    .filter(
      (s) =>
        !lower ||
        (s.studentId || "").toLowerCase().includes(lower) ||
        (s.firstName || "").toLowerCase().includes(lower) ||
        (s.lastName || "").toLowerCase().includes(lower)
    )
    .slice(0, 200);
  const filteredNotes = notes.filter(
    (n) =>
      !lower ||
      (n.noteId || "").toLowerCase().includes(lower) ||
      (n.studentId || "").toLowerCase().includes(lower) ||
      (n.ownerEmail || "").toLowerCase().includes(lower) ||
      (n.topic || "").toLowerCase().includes(lower) ||
      (n.content || "").toLowerCase().includes(lower)
  );

  return (
    <div
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        padding: 12,
        background: "#fafafa",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div style={{ fontWeight: 600 }}>Find Student/Note IDs</div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={{
            padding: "6px 10px",
            background: "#e5e7eb",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
          }}
        >
          {open ? "Hide" : "Show"}
        </button>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              marginBottom: 8,
            }}
          >
            <div
              style={{
                display: "inline-flex",
                background: "#eef2ff",
                borderRadius: 8,
                overflow: "hidden",
              }}
            >
              <button
                type="button"
                onClick={() => setTab("students")}
                style={{
                  padding: "6px 10px",
                  border: "none",
                  background: tab === "students" ? "#6366f1" : "transparent",
                  color: tab === "students" ? "#fff" : "#111827",
                  cursor: "pointer",
                }}
              >
                Students
              </button>
              <button
                type="button"
                onClick={() => setTab("notes")}
                style={{
                  padding: "6px 10px",
                  border: "none",
                  background: tab === "notes" ? "#6366f1" : "transparent",
                  color: tab === "notes" ? "#fff" : "#111827",
                  cursor: "pointer",
                }}
              >
                Notes
              </button>
            </div>
            <input
              placeholder="Search by name, ID, email, topic…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{
                flex: 1,
                minWidth: 240,
                padding: 8,
                borderRadius: 6,
                border: "1px solid #e5e7eb",
              }}
            />
            <button
              type="button"
              onClick={load}
              disabled={loading}
              style={{
                padding: "6px 10px",
                background: "#0ea5e9",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: "pointer",
              }}
            >
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </div>
          {tab === "students" ? (
            <div
              style={{
                maxHeight: 240,
                overflowY: "auto",
                border: "1px solid #e5e7eb",
                borderRadius: 6,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                    <th
                      style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}
                    >
                      Student ID
                    </th>
                    <th
                      style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}
                    >
                      Name
                    </th>
                    <th
                      style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}
                    >
                      Grade
                    </th>
                    <th
                      style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((s) => (
                    <tr key={s.studentId}>
                      <td
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #f1f5f9",
                          fontFamily: "monospace",
                        }}
                      >
                        {s.studentId}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        {s.firstName} {s.lastName}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        {s.grade}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard?.writeText(s.studentId);
                          }}
                          style={{
                            padding: "6px 10px",
                            background: "#e5e7eb",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            marginRight: 6,
                          }}
                        >
                          Copy
                        </button>
                        <button
                          type="button"
                          onClick={() => onUseId(s.studentId)}
                          style={{
                            padding: "6px 10px",
                            background: "#10b981",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                          }}
                        >
                          Use ID
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filteredStudents.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ padding: 10, color: "#6b7280" }}>
                        No matches
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div
              style={{
                maxHeight: 240,
                overflowY: "auto",
                border: "1px solid #e5e7eb",
                borderRadius: 6,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f8fafc", textAlign: "left" }}>
                    <th
                      style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}
                    >
                      Note ID
                    </th>
                    <th
                      style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}
                    >
                      Student ID
                    </th>
                    <th
                      style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}
                    >
                      Owner
                    </th>
                    <th
                      style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}
                    >
                      Topic
                    </th>
                    <th
                      style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}
                    >
                      Created
                    </th>
                    <th
                      style={{ padding: 8, borderBottom: "1px solid #e5e7eb" }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNotes.map((n) => (
                    <tr key={n.noteId}>
                      <td
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #f1f5f9",
                          fontFamily: "monospace",
                        }}
                      >
                        {n.noteId}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #f1f5f9",
                          fontFamily: "monospace",
                        }}
                      >
                        {n.studentId}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        {n.ownerEmail}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        {String(n.topic || "").slice(0, 40)}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        {(n.createdAt || "").slice(0, 19).replace("T", " ")}
                      </td>
                      <td
                        style={{
                          padding: 8,
                          borderBottom: "1px solid #f1f5f9",
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            navigator.clipboard?.writeText(n.noteId);
                          }}
                          style={{
                            padding: "6px 10px",
                            background: "#e5e7eb",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                            marginRight: 6,
                          }}
                        >
                          Copy
                        </button>
                        <button
                          type="button"
                          onClick={() => onUseId(n.noteId)}
                          style={{
                            padding: "6px 10px",
                            background: "#10b981",
                            color: "#fff",
                            border: "none",
                            borderRadius: 6,
                            cursor: "pointer",
                          }}
                        >
                          Use ID
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filteredNotes.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ padding: 10, color: "#6b7280" }}>
                        No matches
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Retention Controls Component
function RetentionControls({ apiUrl, isDistrict }) {
  const [mode, setMode] = React.useState("archive");
  const [years, setYears] = React.useState(5);
  const [schoolCode, setSchoolCode] = React.useState("");
  React.useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const base = apiUrl.replace(/\/$/, "");
        const params =
          isDistrict && schoolCode
            ? `?schoolCode=${encodeURIComponent(schoolCode)}`
            : "";
        const res = await fetch(`${base}/admin/retention-settings${params}`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        });
        if (!res.ok) return; // fallback to defaults
        const data = await res.json();
        if (data.retention) {
          setMode(data.retention.mode);
          if (data.retention.durationDays)
            setYears(Math.round(data.retention.durationDays / 365));
        }
      } catch {}
    })();
  }, [apiUrl, isDistrict, schoolCode]);
  const save = async () => {
    try {
      const token = localStorage.getItem("token");
      const base = apiUrl.replace(/\/$/, "");
      const payload = { mode, durationDays: years * 365 };
      if (isDistrict && schoolCode) payload.schoolCode = schoolCode;
      const res = await fetch(`${base}/admin/retention-settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to save");
      alert("Retention settings saved");
    } catch (e) {
      alert(e.message);
    }
  };
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
        marginTop: 8,
        flexWrap: "wrap",
      }}
    >
      <select
        value={mode}
        onChange={(e) => setMode(e.target.value)}
        style={{ padding: 8, borderRadius: 6, border: "1px solid #e5e7eb" }}
      >
        <option value="archive">Archive after</option>
        <option value="delete">Delete after</option>
      </select>
      <input
        type="number"
        min={1}
        value={years}
        onChange={(e) => setYears(Number(e.target.value || 1))}
        style={{
          width: 80,
          padding: 8,
          borderRadius: 6,
          border: "1px solid #e5e7eb",
        }}
      />
      <span>years</span>
      {isDistrict && (
        <input
          placeholder="School code (optional)"
          value={schoolCode}
          onChange={(e) => setSchoolCode(e.target.value)}
          style={{
            padding: 8,
            borderRadius: 6,
            border: "1px solid #e5e7eb",
            minWidth: 220,
          }}
        />
      )}
      <button
        type="button"
        onClick={save}
        style={{
          padding: "8px 12px",
          background: "#10b981",
          color: "#fff",
          border: "none",
          borderRadius: 6,
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        Save
      </button>
    </div>
  );
}

// Transfer Controls Component
function TransferControls({ apiUrl }) {
  const [studentId, setStudentId] = React.useState("");
  const [targetSchoolCode, setTargetSchoolCode] = React.useState("");
  const transfer = async () => {
    if (!studentId.trim() || !targetSchoolCode.trim())
      return alert("Enter studentId and target school code");
    if (!window.confirm("Transfer this student to the target school?")) return;
    try {
      const token = localStorage.getItem("token");
      const base = apiUrl.replace(/\/$/, "");
      const res = await fetch(`${base}/admin/transfer-student-data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ studentId, targetSchoolCode }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to transfer");
      alert("Student data transferred");
    } catch (e) {
      alert(e.message);
    }
  };
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        alignItems: "center",
        marginTop: 8,
        flexWrap: "wrap",
      }}
    >
      <input
        placeholder="Student ID"
        value={studentId}
        onChange={(e) => setStudentId(e.target.value)}
        style={{
          padding: 8,
          borderRadius: 6,
          border: "1px solid #e5e7eb",
          minWidth: 220,
        }}
      />
      <input
        placeholder="Target School Code"
        value={targetSchoolCode}
        onChange={(e) => setTargetSchoolCode(e.target.value)}
        style={{
          padding: 8,
          borderRadius: 6,
          border: "1px solid #e5e7eb",
          minWidth: 220,
        }}
      />
      <button
        type="button"
        onClick={transfer}
        style={{
          padding: "8px 12px",
          background: "#8b5cf6",
          color: "#fff",
          border: "none",
          borderRadius: 6,
          cursor: "pointer",
          fontWeight: 600,
        }}
      >
        Transfer
      </button>
    </div>
  );
}

// Add School Modal
function AddSchoolModal({ apiUrl, onClose }) {
  const [name, setName] = React.useState("");
  const [address, setAddress] = React.useState("");
  const [contact, setContact] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const create = async () => {
    if (!name.trim()) return alert("Enter a school name");
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${apiUrl.replace(/\/$/, "")}/schools`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : "",
        },
        body: JSON.stringify({ name, address, contact }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create school");
      alert(`School created. Share this code securely: ${data.schoolCode}`);
      onClose();
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 10,
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          padding: 20,
          width: 420,
          maxWidth: "90vw",
        }}
      >
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
          Add School
        </div>
        <div style={{ display: "grid", gap: 8 }}>
          <input
            placeholder="School name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ padding: 8, borderRadius: 6, border: "1px solid #e5e7eb" }}
          />
          <input
            placeholder="Address (optional)"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            style={{ padding: 8, borderRadius: 6, border: "1px solid #e5e7eb" }}
          />
          <input
            placeholder="Contact (optional)"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            style={{ padding: 8, borderRadius: 6, border: "1px solid #e5e7eb" }}
          />
        </div>
        <div
          style={{
            marginTop: 12,
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 12px",
              background: "#e5e7eb",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={create}
            style={{
              padding: "8px 12px",
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            {saving ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

// School Codes Modal
function SchoolCodesModal({ apiUrl, onClose }) {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [schools, setSchools] = React.useState([]);
  const [filter, setFilter] = React.useState("");

  React.useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${apiUrl.replace(/\/$/, "")}/schools`, {
          headers: { Authorization: token ? `Bearer ${token}` : "" },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Failed to load schools");
        if (alive) setSchools(Array.isArray(data.schools) ? data.schools : []);
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [apiUrl]);

  const filtered = React.useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return schools;
    return schools.filter((s) =>
      [s.schoolCode, s.name, s.address, s.contact]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [schools, filter]);

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {}
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 10,
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          padding: 20,
          width: 600,
          maxWidth: "92vw",
          maxHeight: "80vh",
          overflow: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 10,
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 16 }}>School Codes</div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: 20,
              cursor: "pointer",
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <input
          placeholder="Search by code or name..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={{
            width: "100%",
            padding: 8,
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            marginBottom: 10,
          }}
        />
        {loading ? (
          <div style={{ color: "#6b7280" }}>Loading…</div>
        ) : error ? (
          <div style={{ color: "#ef4444" }}>{error}</div>
        ) : filtered.length === 0 ? (
          <div style={{ color: "#6b7280" }}>No schools found.</div>
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {filtered.map((s) => (
              <div
                key={s.schoolCode}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: 10,
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{s.name || "(Unnamed)"}</div>
                  <div style={{ color: "#475569", fontSize: 13 }}>
                    Code: <strong>{s.schoolCode}</strong>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => copy(s.schoolCode)}
                  style={{
                    padding: "6px 10px",
                    background: "#2563eb",
                    color: "#fff",
                    border: "none",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  Copy
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
